import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  styled,
  Fab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { apigetPublic } from '../../service/api';
import Footer from '../../components/Footer/footer';
import DashboardFilterBar, { DashboardNoMatches } from '../../components/dashboard/DashboardFilterBar';
import useDashboardFilters from '../../components/dashboard/useDashboardFilters';
import { unwrapToStringArray } from '../../utils/stringList';

const text = (v) => String(v || '');
const byLast = (a, b) =>
  text(a.lastName).localeCompare(text(b.lastName)) || text(a.firstName).localeCompare(text(b.firstName));
const byFirst = (a, b) =>
  text(a.firstName).localeCompare(text(b.firstName)) || text(a.lastName).localeCompare(text(b.lastName));
const newestFirst = (field) => (a, b) => new Date(b[field] || 0) - new Date(a[field] || 0);

const SORTS = [
  { key: 'last', label: 'Last name A–Z', compare: byLast },
  { key: 'last-desc', label: 'Last name Z–A', compare: (a, b) => byLast(b, a) },
  { key: 'first', label: 'First name A–Z', compare: byFirst },
  { key: 'updated', label: 'Recently updated', compare: newestFirst('updatedAt') },
  { key: 'newest', label: 'Newest added', compare: newestFirst('createdAt') },
];

const DashboardContainer = styled(Container)(() => ({
  paddingTop: '8px',
  paddingBottom: '50px',
}));

const OfficerCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  boxShadow: theme.shadows[3],
  borderRadius: '16px',
  overflow: 'hidden',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[6],
  },
}));

const OfficerMedia = styled(CardMedia)(() => ({
  height: 220,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
}));

const OfficerLink = styled(Link)(() => ({
  textDecoration: 'none',
  color: 'inherit',
}));

function LoanOfficerDashboard() {
  const [officers, setOfficers] = useState([]);
  const [branchesByOfficer, setBranchesByOfficer] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOfficers = async () => {
      const [result, branchRes] = await Promise.all([
        apigetPublic('loanofficer/search'),
        apigetPublic('branches/search'),
      ]);
      if (result?.status === 200 && Array.isArray(result.data?.result)) {
        setOfficers(result.data.result);
      }
      // Branch assignments live on the branch (Edit Branch), so invert them.
      if (branchRes?.status === 200) {
        const map = new Map();
        (branchRes.data?.result || []).forEach((branch) =>
          (branch.loanOfficers || []).forEach((lo) =>
            map.set(lo._id, [...(map.get(lo._id) || []), branch.name])
          )
        );
        setBranchesByOfficer(map);
      }
      setIsLoading(false);
    };
    fetchOfficers();
  }, []);

  const filterConfig = useMemo(
    () => ({
      // Branch and title are searchable rather than separate filters.
      search: (o) => [
        `${text(o.firstName)} ${text(o.lastName)}`,
        o.email,
        o.phone,
        text(o.phone).replace(/\D/g, ''),
        o.nmlsId,
        o.slug,
        o.title,
        ...(branchesByOfficer.get(o._id) || []),
      ],
      filters: [
        { key: 'state', label: 'State', values: (o) => unwrapToStringArray(o.states) },
        { key: 'group', label: 'Group', values: (o) => unwrapToStringArray(o.groups) },
      ],
      sorts: SORTS,
      defaultSort: 'last',
    }),
    [branchesByOfficer]
  );

  const { visible, total, active, clear, barProps } = useDashboardFilters(officers, filterConfig);

  return (
    <>
      <Box
        sx={{
          backgroundImage: 'url(/assets/backgroundimageforherobanner.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'rgba(255,255,255,0.9)',
            minHeight: '100vh',
            zIndex: 1,
            position: 'relative',
          }}
        >
          <DashboardContainer>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              Loan Officer Dashboard
            </Typography>

            <DashboardFilterBar
              noun="loan officers"
              {...barProps}
              shown={visible.length}
              total={total}
              active={active}
              onClear={clear}
            />

            {isLoading ? (
              <Typography align="center">Loading officers...</Typography>
            ) : officers.length > 0 && visible.length === 0 ? (
              <DashboardNoMatches noun="loan officers" onClear={clear} />
            ) : officers.length > 0 ? (
             <Grid container spacing={4}>
  {visible.map((officer, index) => (
    <Grid item xs={12} sm={6} md={4} key={officer._id || index}>
      <OfficerLink to={`/dashboard/loanofficers/${officer.slug}`}>
        <OfficerCard>
          <OfficerMedia
            image={
              officer.imageUrl?.startsWith('http')
                ? officer.imageUrl
                : officer.imageUrl
                ? `https://${officer.imageUrl.replace(/^https?:\/\//, '')}`
                : `/assets/images/avatars/avatar_${(index % 24) + 1}.jpg`
            }
            title={`${officer.firstName} ${officer.lastName}`}
          />
          <CardContent>
            <Typography variant="h6">
              {officer.firstName} {officer.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {officer.title || 'Loan Officer'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {officer.email}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {officer.phone}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Branch: {(branchesByOfficer.get(officer._id) || ['None']).join(', ')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {officer.reviewCount > 0
                ? `${officer.reviewCount} review${officer.reviewCount === 1 ? '' : 's'}`
                : 'No reviews yet'}
            </Typography>
          </CardContent>
        </OfficerCard>
      </OfficerLink>
    </Grid>
  ))}
</Grid>

            ) : (
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 4 }}>
                No loan officers found.
              </Typography>
            )}
          </DashboardContainer>

          {/* Floating Create Button */}
          <Fab
            color="primary"
            aria-label="add"
            onClick={() => navigate('/dashboard/createLO')}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              backgroundColor: '#1976d2',
              color: '#fff',
              '&:hover': { backgroundColor: '#115293' },
            }}
          >
            <AddIcon />
          </Fab>
        </Box>
      </Box>
      <Box sx={(theme) => ({ ...theme.mixins.toolbar })} />
      <Footer />
    </>
  );
}

export default LoanOfficerDashboard;
